import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useSelectedStation } from '@features/stations/hooks/useSelectedStation';
import { RETURN_TO_PARAM } from '@shared/hooks/useReturnTo';
import type { SetupProgress } from '../useSetupProgress';

interface SetupGuideProps {
  progress: SetupProgress;
  /** Where to return after completing a step (deep-linked with `returnTo`). */
  returnTo: string;
  /** Called on navigate, so the container can close the popover. */
  onNavigate?: () => void;
}

/**
 * The setup checklist rows: one per step with a checkbox (checked + struck
 * through when done), emoji, title, "why", and a Go CTA that deep-links to the
 * form that fills it. Presentational — receives already-computed progress.
 */
export function SetupGuide({ progress, returnTo, onNavigate }: SetupGuideProps) {
  const { t } = useTranslation('onboarding');
  const { lng = 'es' } = useParams();
  const navigate = useNavigate();
  const { selectedStationId } = useSelectedStation();

  const go = (to: string) => {
    onNavigate?.();
    void navigate(`${to}?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`);
  };

  return (
    <ul className="flex flex-col gap-1">
      {progress.steps.map((step) => {
        const isNext = step.key === progress.nextStepKey;
        return (
          <li key={step.key}>
            <button
              type="button"
              disabled={step.done}
              onClick={() => go(step.to(lng, selectedStationId))}
              className={[
                'flex w-full items-start gap-3 rounded-xl p-3 text-left transition-colors',
                step.done
                  ? 'cursor-default'
                  : isNext
                    ? 'bg-brand-50 hover:bg-brand-100'
                    : 'hover:bg-slate-50'
              ].join(' ')}
            >
              <span
                className={[
                  'mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-md border-2',
                  step.done
                    ? 'border-signal-ok bg-signal-ok text-white'
                    : 'border-slate-300 bg-white'
                ].join(' ')}
                aria-hidden="true"
              >
                {step.done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
              </span>

              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[15px] leading-none" aria-hidden="true">
                    {step.emoji}
                  </span>
                  <span
                    className={[
                      'text-sm font-semibold',
                      step.done ? 'text-muted line-through' : 'text-ink'
                    ].join(' ')}
                  >
                    {t(`requirements.${step.key}.title`)}
                  </span>
                </span>
                {!step.done ? (
                  <span className="text-xs leading-relaxed text-muted">
                    {t(`requirements.${step.key}.why`)}
                  </span>
                ) : null}
              </span>

              {!step.done ? (
                isNext ? (
                  <span className="flex flex-shrink-0 items-center gap-1 self-center rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-bold text-white">
                    {t(`requirements.${step.key}.cta`)}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                ) : (
                  <ArrowRight
                    className="h-4 w-4 flex-shrink-0 self-center text-brand-600/60"
                    aria-hidden="true"
                  />
                )
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
